import { useCallback, useRef, useState } from 'react'
import { ImageLightbox } from 'components/ui/image-lightbox'
import { cn } from 'lib/utils'

export interface NoteImageCarouselProps {
	urls: string[]
	className?: string
}

/**
 * A note's photographs, one screen at a time.
 *
 * Rendered inline the way a note stores them, five photos from a site visit put
 * a screen and a half between the note's text and the next note down. The
 * desktop answers this with a collage, which needs width it does not have here.
 * A phone has one column, so the axis to spend is horizontal.
 *
 * **Scroll-snap, not a JavaScript carousel.** `snap-x snap-mandatory` with
 * `snap-center` on each slide gives momentum, rubber-banding, trackpad and
 * keyboard scrolling, and correct behaviour under a screen reader, all from the
 * browser. A JS implementation would reimplement each of those worse, and this
 * needs no dependency at all. The dots FOLLOW the scroll position rather than
 * driving it, so the indicator can never disagree with what is on screen.
 *
 * One picture is not a carousel: it renders as a plain image, because dots
 * under a single photo say nothing and a swipe that cannot move reads as broken.
 */
export function NoteImageCarousel({ urls, className }: NoteImageCarouselProps) {
	const [index, setIndex] = useState(0)
	const [lightbox, setLightbox] = useState<string | null>(null)
	const trackRef = useRef<HTMLDivElement>(null)

	/**
	 * Which slide is showing, derived from where the strip has been scrolled.
	 *
	 * Rounding rather than flooring so the dot flips at the halfway point, which
	 * is where the snap will land, instead of only once the next slide is fully
	 * in view.
	 */
	const handleScroll = useCallback(() => {
		const track = trackRef.current
		if (!track || track.clientWidth === 0) return
		const next = Math.round(track.scrollLeft / track.clientWidth)
		setIndex(Math.max(0, Math.min(urls.length - 1, next)))
	}, [urls.length])

	if (urls.length === 0) return null

	if (urls.length === 1) {
		return (
			<>
				<button
					type="button"
					onClick={() => setLightbox(urls[0])}
					className={cn('block w-full', className)}
				>
					<img
						src={urls[0]}
						alt=""
						loading="lazy"
						className="w-full rounded-md object-cover"
					/>
				</button>
				<ImageLightbox
					src={lightbox ?? ''}
					open={Boolean(lightbox)}
					onClose={() => setLightbox(null)}
				/>
			</>
		)
	}

	return (
		<div className={cn('relative', className)}>
			<div
				ref={trackRef}
				onScroll={handleScroll}
				role="group"
				aria-label={`${urls.length} photos`}
				className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
			>
				{urls.map((url, i) => (
					<button
						key={`${url}-${i}`}
						type="button"
						onClick={() => setLightbox(url)}
						aria-label={`Photo ${i + 1} of ${urls.length}`}
						className="w-full shrink-0 snap-center"
					>
						<img
							src={url}
							alt=""
							// The first is what the user sees before scrolling, so it is
							// the one worth not deferring.
							loading={i === 0 ? 'eager' : 'lazy'}
							className="h-48 w-full rounded-md object-cover"
						/>
					</button>
				))}
			</div>

			{/*
				Position, two ways. The dots read at a glance and the count answers
				"how many more", which dots stop conveying past about five.
			*/}
			<div className="mt-1.5 flex items-center justify-center gap-1.5">
				{urls.map((url, i) => (
					<span
						key={`dot-${url}-${i}`}
						aria-hidden="true"
						className={cn(
							'h-1.5 rounded-full transition-all',
							i === index ? 'w-3 bg-primary' : 'w-1.5 bg-gray-300',
						)}
					/>
				))}
				<span className="ml-1 text-[11px] tabular-nums text-gray-400">
					{index + 1}/{urls.length}
				</span>
			</div>

			<ImageLightbox
				src={lightbox ?? ''}
				open={Boolean(lightbox)}
				onClose={() => setLightbox(null)}
			/>
		</div>
	)
}

export default NoteImageCarousel
